import React from "react";
import { Box, Tab, Tabs, useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { LocationStatus } from "../../../../types/LocationStatus";
import { LocationTabConfig } from "./LocationTabConfig";
import LocationTabBadge from "./LocationTabBadge";

interface LocationTabBarProps {
  tabs: LocationTabConfig[];
  counts: Record<LocationStatus, number>;
  activeTab: LocationStatus;
  onTabChange: (variant: LocationStatus) => void;
}

/**
 * Container. On mobile, a right-edge gradient fade signals hidden tabs.
 * `position: relative` is required for the pseudo-element overlay.
 */
const TabBarRoot = styled(Box)(({ theme }) => ({
  borderBottom: "2px solid #e0e4e8",
  marginBottom: "20px",
  position: "relative",
  // Scroll-hint gradient — only visible on mobile
  [theme.breakpoints.down("sm")]: {
    "&::after": {
      content: '""',
      position: "absolute",
      right: 0,
      top: 0,
      bottom: 2,              // sit above the 2px border
      width: "48px",
      background: "linear-gradient(to right, transparent, rgba(255,255,255,0.95))",
      pointerEvents: "none",
      zIndex: 1,
    },
  },
}));

const StyledTabs = styled(Tabs)({
  minHeight: "unset",
  "& .MuiTabs-indicator": {
    height: 3,
    backgroundColor: "#3f4fc1",
  },
  // Show a thin scrollbar on mobile so scroll is discoverable
  "& .MuiTabs-scroller": {
    overflowX: "auto !important",
    "&::-webkit-scrollbar": { height: 3 },
    "&::-webkit-scrollbar-thumb": { background: "#d0d8e0", borderRadius: 4 },
  },
});

const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: "unset",
  padding: "11px 20px",
  fontSize: "0.9643rem",   // 13.5px at 14px root
  fontWeight: 500,
  color: "#7a8a9a",
  textTransform: "none",
  whiteSpace: "nowrap",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "7px",
  "&.Mui-selected": {
    color: "#3f4fc1",
    fontWeight: 700,
  },
  "&:hover": {
    color: "#3f4fc1",
  },
  // Mobile: fixed width so ~2.4 tabs are visible → scroll is self-evident
  [theme.breakpoints.down("sm")]: {
    minWidth: "42vw",
    flexShrink: 0,
  },
  // SM+: let tabs fill the bar equally
  [theme.breakpoints.up("sm")]: {
    flex: 1,
    minWidth: "unset",
  },
}));

/**
 * Renders the horizontal tab strip for the location list.
 *
 * - **Mobile** (`xs`): `variant="scrollable"` — tabs have a fixed minimum width
 *   so ~2.4 are visible, making horizontal scrolling immediately apparent.
 *   A right-edge gradient fade reinforces the scroll affordance.
 * - **SM+**: `variant="fullWidth"` — tabs stretch equally to fill the bar.
 */
const LocationTabBar = ({ tabs, counts, activeTab, onTabChange }: LocationTabBarProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <TabBarRoot>
      <StyledTabs
        value={activeTab}
        onChange={(_, value: LocationStatus) => onTabChange(value)}
        variant={isMobile ? "scrollable" : "fullWidth"}
        scrollButtons={false}
        aria-label="Location status tabs"
      >
        {tabs.map((tab) => (
          <StyledTab
            key={tab.variant}
            value={tab.variant}
            label={
              <Box display="flex" alignItems="center" gap="7px">
                {tab.icon}
                {tab.label}
                <LocationTabBadge
                  variant={tab.variant}
                  count={counts[tab.variant]}
                  isTabActive={activeTab === tab.variant}
                />
              </Box>
            }
            aria-label={`${tab.label} locations (${counts[tab.variant]})`}
          />
        ))}
      </StyledTabs>
    </TabBarRoot>
  );
};

export default LocationTabBar;
